/**
 * Utilities for mapping timeline slider items and handling "change mode".
 *
 * These helpers centralize logic that appears in both `KlimaSlider` and
 * `KlimaSliderWrapper` so the matching behavior is consistent and easier to
 * maintain.
 */

// Regex to capture a year-range like "2031-2060" from labels/values.
const YEAR_RANGE_RE = /\d{4}-\d{4}/;

/**
 * Try to extract a YYYY-YYYY year-range from an item.
 * The function looks through common candidate fields in order:
 * `value`, `label`, and `short` and returns the first matched range.
 *
 * @param {object} item - Slider item which may include `value`, `label`, `short`.
 * @returns {string|null} First matched year-range (e.g. "2031-2060") or `null`.
 */
export function extractYearFromItem(item) {
  if (!item) return null;
  const candidates = [item.value, item.label, item.short];
  for (const c of candidates) {
    if (c && typeof c === "string") {
      const m = c.match(YEAR_RANGE_RE);
      if (m) return m[0];
    }
  }
  return null;
}

/**
 * Build two Maps that link between "base" projection items and their
 * corresponding excluded "change" items (the ones with `excludeInSlider===true`).
 *
 * The maps are keyed by the original array index so callers can work with the
 * original `items` array indexes used throughout the components.
 *
 * @param {Array} items - Array of slider items (original items from the parent)
 * @returns {Object} { baseToChangeMap: Map, changeToBaseMap: Map }
 */
export function buildBaseChangeMaps(items = []) {
  const baseToChangeMap = new Map();
  const changeToBaseMap = new Map();

  // Iterate items and try to find pairs where one item is a change/derivative
  // (marked by `excludeInSlider`) and another contains the same year-range
  // but is not excluded (the visible base item).
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it || !it.label) continue;
    if (it.excludeInSlider === true) {
      const m = String(it.label).match(YEAR_RANGE_RE);
      if (m && m[0]) {
        const yr = m[0];
        const baseIndex = items.findIndex(
          (x, idx) =>
            idx !== i &&
            x &&
            String(x.label).includes(yr) &&
            x.excludeInSlider !== true
        );
        if (baseIndex !== -1) {
          changeToBaseMap.set(i, baseIndex);
          baseToChangeMap.set(baseIndex, i);
        }
      }
    }
  }
  return { baseToChangeMap, changeToBaseMap };
}

/**
 * Find the first visible slider item that has `used === true` and return its
 * index in the original items array.
 *
 * The function accepts either the full `originalItems` array alone or both
 * `originalItems` and a precomputed `sliderItems` (the visible subset with
 * `excludeInSlider !== true`). Passing `sliderItems` avoids recomputing the
 * filter when the caller already has it.
 *
 * @param {Array} originalItems
 * @param {Array} [sliderItems]
 * @returns {number} original index or -1 when none found
 */
export function findFirstUsableOriginalIndex(originalItems = [], sliderItems) {
  const view =
    sliderItems ||
    originalItems.filter((it) => it && it.excludeInSlider !== true);
  for (let i = 0; i < view.length; i++) {
    if (view[i].used) return originalItems.indexOf(view[i]);
  }
  return -1;
}

/**
 * Find the next usable original index after `currentOriginalIndex` in the
 * visible `sliderItems` (wraps around). Returns -1 if no usable item found.
 *
 * @param {Array} originalItems
 * @param {number} currentOriginalIndex
 * @param {Array} [sliderItems]
 * @returns {number}
 */
export function findNextUsableOriginalIndex(
  originalItems = [],
  currentOriginalIndex,
  sliderItems
) {
  const view =
    sliderItems ||
    originalItems.filter((it) => it && it.excludeInSlider !== true);
  if (!view.length) return -1;
  const currentPos = view.findIndex(
    (it) => originalItems.indexOf(it) === currentOriginalIndex
  );
  const start = currentPos === -1 ? -1 : currentPos;
  for (let i = 1; i <= view.length; i++) {
    const idx = (start + i) % view.length;
    if (view[idx].used) return originalItems.indexOf(view[idx]);
  }
  return -1;
}

/**
 * Find the last usable observation (non-projection) item in the visible
 * `sliderItems` and return its index in the original items array.
 *
 * @param {Array} originalItems
 * @param {Array} [sliderItems]
 * @returns {number}
 */
export function findLastUsableObservationOriginalIndex(
  originalItems = [],
  sliderItems
) {
  const view =
    sliderItems ||
    originalItems.filter((it) => it && it.excludeInSlider !== true);
  for (let i = view.length - 1; i >= 0; i--) {
    if (!view[i].projection && view[i].used)
      return originalItems.indexOf(view[i]);
  }
  return -1;
}

/**
 * Determine which original item index should be selected when toggling
 * "change mode" on/off. The function implements the same heuristics used
 * previously in multiple places:
 *
 * - When enabling change-mode: prefer a structurally matched excluded
 *   change-item that shares the year-range, fall back to a legacy label
 *   concatenation of the form `"<label> zu 1971-2000"`.
 * - When disabling change-mode: if the current item is an excluded change
 *   item try to map back to its base projection using maps/year matching and
 *   finally fallback to removing the suffix in the label.
 *
 * All returned indexes are indexes into the original `items` array so they
 * can be directly used by parent components.
 *
 * @param {Array} items - original items array
 * @param {number} currentIndex - currently selected original index
 * @param {boolean} enabled - whether change-mode will be enabled
 * @returns {number} new original index to select (may be the same as input)
 */
export function mapIndexForChangeMode(items = [], currentIndex, enabled) {
  const { changeToBaseMap } = buildBaseChangeMaps(items);
  const currentItem = items && items[currentIndex];
  const year = extractYearFromItem(currentItem);

  if (enabled) {
    // If already a change-item nothing to change.
    if (currentItem && currentItem.excludeInSlider === true)
      return currentIndex;

    // Prefer structural match: find excluded change-item with same year-range.
    if (year) {
      const changeIndex = items.findIndex(
        (it, idx) =>
          idx !== currentIndex &&
          it &&
          it.excludeInSlider === true &&
          extractYearFromItem(it) === year
      );
      if (changeIndex !== -1) return changeIndex;
    }

    // Fallback: legacy label-based concatenation.
    if (currentItem && currentItem.label) {
      const changeLabel = `${currentItem.label} zu 1971-2000`;
      const idx = items.findIndex((it) => it && it.label === changeLabel);
      if (idx !== -1) return idx;
    }

    return currentIndex;
  } else {
    // Disabling: map back from change-item to base if possible.
    if (currentItem && currentItem.excludeInSlider === true) {
      if (changeToBaseMap.has(currentIndex))
        return changeToBaseMap.get(currentIndex);

      if (year) {
        const baseIndex = items.findIndex(
          (it) =>
            it &&
            it.excludeInSlider !== true &&
            extractYearFromItem(it) === year
        );
        if (baseIndex !== -1) return baseIndex;
      }

      // Fallback: remove suffix from label
      if (currentItem.label && currentItem.label.includes(" zu 1971-2000")) {
        const baseLabel = currentItem.label.replace(" zu 1971-2000", "");
        const baseIndex = items.findIndex((it) => it && it.label === baseLabel);
        if (baseIndex !== -1) return baseIndex;
      }
    }
    return currentIndex;
  }
}

/**
 * Return true when the item at `index` should be treated as a change-mode
 * (Änderung zu 1971-2000) item.
 * Heuristic: `excludeInSlider === true` OR label contains the legacy
 * suffix "zu 1971-2000".
 *
 * @param {Array} items
 * @param {number} index
 * @returns {boolean}
 */
export function isChangeModeForIndex(items = [], index) {
  if (!items || typeof index !== "number" || index === -1) return false;
  const it = items[index];
  if (!it) return false;
  if (it.excludeInSlider === true) return true;
  if (it.label && String(it.label).includes("zu 1971-2000")) return true;
  return false;
}
